import type { EligibilityFileJobLog } from './eligibility-file-job-log.type.ts';
import {
  ABORTED_STATUSES,
  AWAITING_APPROVAL_STATUSES,
  CAN_REPROCESS_STATUSES,
  UNABORTABLE_STATUSES,
} from './eligibility-file-job-log.constants.ts';

export interface EligibilityFileJobLogExtension extends EligibilityFileJobLog {}
export class EligibilityFileJobLogExtension {
  // ── Computed Properties ──
  get abortedAt() {
    return this.statusHistory.find((status) =>
      (ABORTED_STATUSES as readonly string[]).includes(status.status)
    )?.updatedAt;
  }

  get isRunning() {
    return this.statusHistory.some((update) => update.status === 'EXECUTING_TASKS');
  }

  get isUploading() {
    return this.status === 'WAITING_FOR_UPLOAD';
  }

  get canProcess() {
    return (AWAITING_APPROVAL_STATUSES as readonly string[]).includes(this.status);
  }

  get canReprocess() {
    return (CAN_REPROCESS_STATUSES as readonly string[]).includes(this.status);
  }

  get canSchedule() {
    return (AWAITING_APPROVAL_STATUSES as readonly string[]).includes(this.status);
  }

  get canAbort() {
    return !(UNABORTABLE_STATUSES as readonly string[]).includes(this.status);
  }

  get hasAborted() {
    return (ABORTED_STATUSES as readonly string[]).includes(this.status);
  }

  get totalProcessed() {
    const { previewCounts, counts } = this;

    if (!previewCounts || !counts) return '0%';

    const preview =
      previewCounts.created +
      previewCounts.updated +
      previewCounts.deactivated +
      previewCounts.reactivated;

    const processed = counts.created + counts.updated + counts.deactivated + counts.reactivated;

    const total = Math.floor((processed / preview) * 100);

    return `${isNaN(total) ? 0 : total}%`;
  }

  get totalUnchanged() {
    const { previewCounts } = this;

    return previewCounts?.unchanged ?? 0;
  }

  get autoProcessEnabled() {
    // This makes a huge assumption that the company relation is loaded
    if (!this.company.companySetting) {
      return true;
    }
    const eligibilityDefault = this.company.companySetting.eligibilityDefault;
    // Prefer the camelCase key tio-api now serves/stores verbatim; fall back to the
    // legacy dasherized key for data served by the pre-opaque formatter.
    return eligibilityDefault.autoProcess ?? eligibilityDefault['auto-process'];
  }

  /** The pivot code(s) stamped on `header` (`"{codes}::{originalHeader}"`), or `[]` when unpivoted. */
  get pivotCodes() {
    if (!this.header?.includes('::')) return [];
    return this.header
      .split('::')[0]!
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean);
  }

  /** `pivotCodes` reverse-mapped to their configured raw values (a code may map from several). */
  get pivots(): string[] {
    const codes = this.pivotCodes;
    if (!codes.length) return [];

    const postProcess = this.company.companySetting?.pivotAttribute?.postProcess;
    if (!postProcess) return codes;

    let parsed: unknown;
    try {
      parsed = JSON.parse(postProcess.slice('pivot('.length, -1));
    } catch {
      return codes;
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return codes;

    const valuesByCode = new Map<string, string[]>();
    for (const [rawValue, code] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof code !== 'string') continue;
      valuesByCode.set(code, [...(valuesByCode.get(code) ?? []), rawValue]);
    }

    return codes.flatMap((code) => valuesByCode.get(code) ?? [code]);
  }
}

const Registration = {
  kind: 'object',
  name: 'eligibility-file-job-log',
  features: EligibilityFileJobLogExtension,
};
export default Registration;
